/**
 * CountrySelectorPage — Reached from the "قاعدة بيانات الرخص التجارية و
 * الأوراق" card. Reads active countries from the Company Setup Inputs
 * database and renders them as cards.
 *
 * (iter-220, Feb 2026)
 *
 * NOTE — hard-coded routes: for now only the UAE has a downstream
 * emirates-grid page (`/control/company-setup/database/uae`). Any future
 * country will need its own downstream page + a `route` mapping below.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Globe, MapPin, Loader2 } from 'lucide-react';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import DashboardHeader from '../components/DashboardHeader';
import BreadcrumbNav from '../components/BreadcrumbNav';

const API = process.env.REACT_APP_BACKEND_URL;

// Downstream routes per country code. If a country isn't here, the card
// is rendered as coming-soon (disabled + gray).
const COUNTRY_ROUTE_MAP = {
  UAE: '/control/company-setup/database/uae',
};

// Optional per-country colour for the icon backplate.
const COUNTRY_COLOR_MAP = {
  UAE: '#10B981',
};

export default function CountrySelectorPage() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const token = sessionStorage.getItem('admin_token') || localStorage.getItem('admin_token');
        const { data } = await axios.get(`${API}/api/admin/company-setup-inputs?level=country&include_inactive=false`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setCountries(data?.data || []);
      } catch (e) {
        console.error('countries load failed', e);
        setCountries([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  return (
    <div className="min-h-screen bg-[#030303]">
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-cyan-500/5 rounded-full blur-[150px]" />
      </div>

      <DashboardHeader showBackButton={true} backPath="/control/company-setup" />

      <div className="border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <BreadcrumbNav items={[
            { label_ar: 'لوحة التحكم', label_en: 'Dashboard', path: '/dashboard' },
            { label_ar: 'CLOUDGM FZ LLC', label_en: 'CLOUDGM FZ LLC', path: '/dashboard?view=cloudgm' },
            { label_ar: 'منصة العملاء', label_en: 'Customers Platform', path: '/dashboard?view=cloudgm&platform=customers' },
            { label_ar: 'التحكم والمعلومات (منصة العملاء)', label_en: 'Control & Info (Customers)', path: '/control/cloudgm' },
            { label_ar: 'تأسيس الشركات', label_en: 'Company Setup', path: '/control/company-setup' },
            { label_ar: 'قاعدة بيانات الرخص التجارية و الأوراق', label_en: 'Trade Licenses Database', path: null }
          ]} />
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center gap-3 mb-2">
          <Globe className="h-6 w-6 text-cyan-400" />
          <h1 className="text-2xl font-bold text-white">
            {isAr ? 'اختر الدولة' : 'Choose Country'}
          </h1>
        </div>
        <p className="text-[#71717A]">
          {isAr ? 'اختر الدولة لعرض جهاتها' : 'Pick a country to browse its authorities'}
        </p>
      </div>

      <main className="relative z-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-cyan-400" />
            </div>
          ) : countries.length === 0 ? (
            <div className="rounded-2xl border border-white/10 bg-[#151530] p-12 text-center text-[#71717A]">
              {isAr ? 'لا توجد دول مفعّلة حالياً. أضف دولة من قائمة المدخلات.' : 'No active countries yet. Add one from the Inputs list.'}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {countries.map((country, idx) => {
                const route = COUNTRY_ROUTE_MAP[country.code];
                const color = COUNTRY_COLOR_MAP[country.code] || '#3B9EDC';
                const enabled = !!route;
                return (
                  <motion.button
                    key={country.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.05 }}
                    whileHover={enabled ? { y: -3, scale: 1.02 } : {}}
                    disabled={!enabled}
                    onClick={() => enabled && navigate(route)}
                    className={`group relative overflow-hidden rounded-2xl border p-6 text-start transition-colors ${
                      enabled
                        ? 'border-white/10 bg-gradient-to-br from-[#151530] to-[#0f0f24] hover:border-white/25 cursor-pointer'
                        : 'border-white/5 bg-[#0a0a0a]/50 opacity-50 cursor-not-allowed'
                    }`}
                    data-testid={`country-card-${country.code || country.id}`}
                  >
                    <div
                      className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                      style={{ backgroundColor: `${color}20`, border: `1px solid ${color}44` }}
                    >
                      <MapPin className="h-6 w-6" style={{ color }} />
                    </div>
                    <h3 className="text-lg font-bold text-white mb-1">
                      {isAr ? country.name_ar : country.name_en}
                    </h3>
                    <p className="text-xs text-[#71717A]">
                      {enabled
                        ? (isAr ? 'انقر للدخول' : 'Tap to open')
                        : (isAr ? 'قريباً' : 'Coming soon')}
                    </p>
                  </motion.button>
                );
              })}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
